// The address a checkout was started with, kept for the moment the provider
// sends the visitor back. §12 forbids it in a URL, so the tab remembers it.
const KEY = 'kratispot.checkout';

export type StartedCheckout = {
  readonly slug: string;
  readonly code: string;
  readonly email: string;
  // The day booked. A real provider redirects to one fixed URL of its own and
  // carries nothing of ours, so without this the visitor lands on today. §8
  readonly date?: string;
};

// Private-mode browsers throw on storage; the return then simply says less.
export function rememberCheckout(started: StartedCheckout): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(started));
  } catch {
    return;
  }
}

export function recallCheckout(slug: string, code: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const kept = JSON.parse(raw) as Partial<StartedCheckout>;
    const mine = kept.slug === slug && kept.code === code && typeof kept.email === 'string';
    return mine ? (kept.email as string) : null;
  } catch {
    return null;
  }
}

// A provider that names no booking on its way back leaves only the tab. §12
export function recallAnyCheckout(): StartedCheckout | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const kept = JSON.parse(raw) as Partial<StartedCheckout>;
    const whole =
      typeof kept.slug === 'string' &&
      typeof kept.code === 'string' &&
      typeof kept.email === 'string';
    return whole ? (kept as StartedCheckout) : null;
  } catch {
    return null;
  }
}

export function forgetCheckout(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    return;
  }
}

// The booking this tab holds, so the map can mark it as the guest's own. §12
const MINE = 'kratispot.booking';

export function rememberOwnBooking(booking: StartedCheckout): void {
  try {
    window.sessionStorage.setItem(MINE, JSON.stringify(booking));
  } catch {
    return;
  }
}

export function recallOwnBooking(slug: string): StartedCheckout | null {
  try {
    const raw = window.sessionStorage.getItem(MINE);
    if (raw === null) return null;
    const kept = JSON.parse(raw) as Partial<StartedCheckout>;
    const mine =
      kept.slug === slug && typeof kept.code === 'string' && typeof kept.email === 'string';
    return mine ? (kept as StartedCheckout) : null;
  } catch {
    return null;
  }
}

export function forgetOwnBooking(): void {
  try {
    window.sessionStorage.removeItem(MINE);
  } catch {
    return;
  }
}
